import React, { useEffect, useState } from 'react';
import { Download, FileUp, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { AccountReport } from '../../types';
import { formatDate, formatFileSize } from '../../lib/format';
import { useUpload } from '../../lib/useUpload';
import {
  Spinner, EmptyState, Modal, Alert, ConfirmDialog, UploadProgress,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

export default function AccountReports() {
  const [reports, setReports] = useState<AccountReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const upload = useUpload();
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<AccountReport[]>('/account-reports')
      .then(setReports)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('description', description);
      fd.append('file', file);
      await upload.run('/account-reports', fd);
      setModalOpen(false);
      setTitle('');
      setDescription('');
      setFile(null);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/account-reports/${deleteId}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold">Account Reports</h1>
          <p className="text-sm text-gray-500">Uploaded account reports are visible only to the admin.</p>
        </div>
        <button onClick={() => { setError(''); setModalOpen(true); }} className={primaryBtn}>
          <FileUp className="h-4 w-4" /> Upload Report
        </button>
      </div>

      {loading ? (
        <Spinner label="Loading reports..." />
      ) : reports.length === 0 ? (
        <EmptyState message="No account reports uploaded yet." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3 text-gray-500">{r.description || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{r.filename}</td>
                  <td className="px-4 py-3 text-gray-500">{formatFileSize(r.size)}</td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <a
                        href={`/api/account-reports/${r.id}/download`}
                        download
                        className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-300 text-xs hover:bg-gray-50"
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                      </a>
                      <button onClick={() => setDeleteId(r.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Upload Account Report">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className={labelClass}>Title *</label>
            <input className={inputClass} required value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>File (PDF, Excel, CSV, Word or image) *</label>
            <input
              type="file"
              accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,image/*"
              className={inputClass}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <p className="text-xs text-gray-500">The uploaded report can only be viewed by the admin.</p>
          <UploadProgress
            percent={upload.percent}
            phase={upload.phase}
            error={upload.error}
            onCancel={upload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={upload.busy} className={primaryBtn}>
              {upload.phase === 'uploading'
                ? `Uploading ${upload.percent}%`
                : upload.phase === 'processing' ? 'Processing...' : 'Upload'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Report"
        message="Are you sure you want to delete this account report?"
      />
    </div>
  );
}
